// M36 — User-preference context. Currently exposes the two
// user-controllable display preferences that the rest of the app
// reads: date format and default landing page. The full
// `user_settings` row (tax rate, paperwork-form prefs, etc.) is still
// fetched and saved directly from SettingsPage — this context is just
// the slice that other pages need to react to.
//
// M54b — default landing page for users without an explicit
// preference is now time-gated. Users created on or after
// NEW_USER_INVENTORY_CUTOVER default to Inventory (Plan B's first-run
// reframing); users created before keep landing on Dashboard. Users
// with an explicit `default_landing_page` value in `user_settings`
// always win — the cutover only affects the implicit fallback.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { formatDate } from '../lib/formatDate';
import type { DateFormat, LandingPage, MagTrackingPreference } from '../lib/types';

// M54b — Cutover for "default to Inventory for first-time users."
// Users created BEFORE this date keep landing on Dashboard so we
// don't move existing testers under them. Users created on or after
// land on Inventory. Reversible by changing the constant or removing
// the gate. Set to the actual Phase 2 deploy timestamp at merge.
// eslint-disable-next-line react-refresh/only-export-components
export const NEW_USER_INVENTORY_CUTOVER = new Date('2026-06-05T00:00:00Z');

/**
 * The implicit landing page for a user without an explicit
 * `default_landing_page` setting. Returns `'dashboard'` when there's
 * no user (signed-out / pre-auth render) or the user was created
 * before the cutover; returns `'inventory'` for users created on or
 * after the cutover.
 *
 * Exported for tests; not used outside this file in production.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function defaultLanding(user: User | null | undefined): LandingPage {
  if (!user) return 'dashboard';
  const created = new Date(user.created_at);
  // Malformed `created_at` (NaN) → fall back to Dashboard (safe
  // default — never silently move an unknown user to a new page).
  if (Number.isNaN(created.getTime())) return 'dashboard';
  return created >= NEW_USER_INVENTORY_CUTOVER ? 'inventory' : 'dashboard';
}

interface UserSettingsContextValue {
  dateFormat: DateFormat;
  landingPage: LandingPage;
  /** M75a — how NEW magazine groups are born. Missing row/field ⇒ 'ask'
   *  (the same no-row fallback contract as date_format above). */
  magTrackingDefault: MagTrackingPreference;
  /** M78b — the venue whose FFL details you cite when a seller needs them.
   *  null when never chosen, or when the venue was deleted (the FK is
   *  ON DELETE SET NULL). Same no-row fallback contract as the fields above. */
  preferredFflVenueId: string | null;
  loading: boolean;
  /** Re-fetch settings from the DB. SettingsPage calls this after a
   * save so the rest of the app picks up the new preference without a
   * full reload. */
  refresh: () => Promise<void>;
}

const UserSettingsContext = createContext<UserSettingsContextValue>({
  dateFormat: 'us_long',
  landingPage: 'dashboard',
  magTrackingDefault: 'ask',
  preferredFflVenueId: null,
  loading: true,
  refresh: async () => { /* noop */ },
});

export function UserSettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [dateFormat, setDateFormat] = useState<DateFormat>('us_long');
  const [landingPage, setLandingPage] = useState<LandingPage>('dashboard');
  const [magTrackingDefault, setMagTrackingDefault] = useState<MagTrackingPreference>('ask');
  const [preferredFflVenueId, setPreferredFflVenueId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      // Signed-out / pre-auth render: fall back to defaults and stop
      // showing the loading spinner. Public routes don't read these
      // values anyway.
      setDateFormat('us_long');
      setLandingPage('dashboard');
      setMagTrackingDefault('ask');
      setPreferredFflVenueId(null);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('user_settings')
      .select('date_format, default_landing_page, mag_tracking_default, preferred_ffl_venue_id')
      .eq('user_id', user.id)
      .maybeSingle();
    // Both branches (row present, row missing) fall through the same
    // fallbacks: stored value wins, otherwise time-gated default.
    // The implicit "no row" case is critical for M54b — new
    // post-cutover users won't have a user_settings row until they
    // first visit Settings, and they need to land on Inventory in
    // the meantime.
    setDateFormat((data?.date_format as DateFormat) ?? 'us_long');
    setLandingPage((data?.default_landing_page as LandingPage) ?? defaultLanding(user));
    setMagTrackingDefault((data?.mag_tracking_default as MagTrackingPreference) ?? 'ask');
    setPreferredFflVenueId(data?.preferred_ffl_venue_id ?? null);
    setLoading(false);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <UserSettingsContext.Provider value={{ dateFormat, landingPage, magTrackingDefault, preferredFflVenueId, loading, refresh }}>
      {children}
    </UserSettingsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserSettings() {
  return useContext(UserSettingsContext);
}

/**
 * Returns a `format(date)` function bound to the current user's
 * preferred date format. Accepts a Date or a date string. Use this
 * everywhere user-visible dates are rendered.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFormatDate() {
  const { dateFormat } = useContext(UserSettingsContext);
  return (date: Date | string) => formatDate(date, dateFormat);
}
